import React, { useCallback, useEffect, useRef, useState } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { doesFocusableExist } from '@noriginmedia/norigin-spatial-navigation';

import {
  headerActiveAtom,
  isErrorDialogOpenAtom,
  npvrFailedDialogResolvedAtom,
  npvrNotificationStateAtom
} from '@smart-tv/atoms';
import RecordingDialog from '@smart-tv/components/dialog/recording-dialog';
import { HEADER_SETTINGS } from '@smart-tv/components/header/header-helpers';
import { links } from '@smart-tv/components/header/header-navigation';
import useVodPlayer from '@smart-tv/components/player/vod-wrapper/_hooks/use-vod-player';
import useDialog from '@smart-tv/hooks/use-dialog';
import useQuery from '@smart-tv/hooks/use-query';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';

import { NpvrTab } from './npvr-helpers';

export const npvrFailedDialogResetAtom = atom(0);
const SETTINGS_PATH = '/settings';

const FRONTPAGE_PATH = '/';
const FIRST_STRIPE_FIRST_ITEM_FOCUS_KEY = '0-0';

enum NpvrNotificationDialog {
  Failed = 'FAILED',
  Scheduled = 'SCHEDULED'
}

const dialogContent: Record<
  NpvrNotificationDialog,
  { titleText: string; description: string; secondaryButtonText: string; targetTab: NpvrTab }
> = {
  [NpvrNotificationDialog.Failed]: {
    titleText: 'Der er optagelser, som er fejlet.',
    description: 'En eller flere af dine planlagte optagelser er desværre fejlet.',
    secondaryButtonText: 'Gå til optagelser',
    targetTab: NpvrTab.Errors
  },
  [NpvrNotificationDialog.Scheduled]: {
    titleText: 'Der er ikke plads til alle dine planlagte optagelser.',
    description:
      'Dine planlagte optagelser overstiger grænsen på dine tilgængelige timer. Frigør plads til nye optagelser ved at annullere planlagte optagelser eller slette gemte optagelser.',
    secondaryButtonText: 'Frigør',
    targetTab: NpvrTab.Recordings
  }
};

export const NpvrFailedRecordingsDialog = () => {
  const resetVersion = useAtomValue(npvrFailedDialogResetAtom);
  const notificationState = useAtomValue(npvrNotificationStateAtom);
  const errorOpen = useAtomValue(isErrorDialogOpenAtom);
  const setHeaderActive = useSetAtom(headerActiveAtom);
  const setResolved = useSetAtom(npvrFailedDialogResolvedAtom);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { programsheet } = useQuery<{ programsheet?: string }>();
  const { isPlaying } = useVodPlayer();
  const { opened: otherDialogOpen, handleSetDialogFocusKey } = useDialog();
  const { markDialogSeen } = useNpvrNotifications();

  const [dialog, setDialog] = useState<NpvrNotificationDialog | null>(null);
  const shownRef = useRef(new Set<string>());

  const isOnFrontpage = pathname === FRONTPAGE_PATH;
  const hasFailedRecordings = !!notificationState && notificationState.dialogFailedEventsShown;
  const hasMissingTime = !!notificationState && notificationState.dialogScheduledEventsShown;

  const getPendingDialog = useCallback(() => {
    if (hasFailedRecordings && !shownRef.current.has(NpvrNotificationDialog.Failed)) {
      return NpvrNotificationDialog.Failed;
    }
    if (hasMissingTime && !shownRef.current.has(NpvrNotificationDialog.Scheduled)) {
      return NpvrNotificationDialog.Scheduled;
    }
    return null;
  }, [hasFailedRecordings, hasMissingTime]);

  useEffect(() => {
    shownRef.current.clear();
    setResolved(false);
    return () => setResolved(false);
  }, [resetVersion, setResolved]);

  useEffect(() => {
    if (dialog || !notificationState) return;

    const pendingDialog = getPendingDialog();
    if (!pendingDialog) {
      setResolved(true);
      return;
    }
    if (!isOnFrontpage || programsheet || isPlaying || errorOpen || otherDialogOpen) return;

    shownRef.current.add(pendingDialog);
    setResolved(false);
    setDialog(pendingDialog);
    markDialogSeen(pendingDialog);
  }, [
    resetVersion,
    notificationState,
    dialog,
    getPendingDialog,
    isOnFrontpage,
    programsheet,
    isPlaying,
    errorOpen,
    otherDialogOpen,
    markDialogSeen,
    setResolved
  ]);

  useEffect(() => {
    if (!isOnFrontpage) shownRef.current.clear();
    if (!isOnFrontpage && dialog) {
      setDialog(null);
      setResolved(true);
    }
  }, [isOnFrontpage, dialog, setResolved]);

  const handleClose = useCallback(() => {
    if (doesFocusableExist(FIRST_STRIPE_FIRST_ITEM_FOCUS_KEY)) {
      handleSetDialogFocusKey(FIRST_STRIPE_FIRST_ITEM_FOCUS_KEY);
    }
    setDialog(null);
    setResolved(!getPendingDialog());
  }, [handleSetDialogFocusKey, setResolved, getPendingDialog]);

  const handleGoToRecordings = useCallback(() => {
    if (!dialog) return;

    handleSetDialogFocusKey(HEADER_SETTINGS);
    setDialog(null);
    setResolved(true);
    setHeaderActive(links.findIndex((link) => link.href === SETTINGS_PATH));
    navigate(SETTINGS_PATH, {
      state: { openNpvrTab: dialogContent[dialog].targetTab }
    });
  }, [navigate, setHeaderActive, handleSetDialogFocusKey, setResolved, dialog]);

  if (!dialog || !isOnFrontpage || errorOpen) return null;

  const content = dialogContent[dialog];

  return (
    <RecordingDialog
      dialogProps={{
        titleText: content.titleText,
        description: content.description,
        primaryButtonText: 'OK',
        primaryButtonFunction: handleClose,
        secondaryButtonText: content.secondaryButtonText,
        secondaryButtonFunction: handleGoToRecordings
      }}
      onClose={handleClose}
    />
  );
};

export default NpvrFailedRecordingsDialog;
